import { useEffect, useState } from "react";

import { Form, Input, InputNumber, Select, Modal, Switch, ConfigProvider } from "antd";

import { useTranslation } from "react-i18next";

import PropTypes from "prop-types";

import apiClient from "../../helpers/apiClient";
import { regularAddonApi } from "./lib/addonApi";
import customizeRequiredMark from "../../components/custom_antd/CustomizeRequiredMark";
import ImageUpload from "../cleaner/components/ImageUpload";
import uploadFile from "../../utilities/uploadFile";
import deleteFile from "../../utilities/deleteFile";
import { useToast } from "../../contexts/ToastContext";

const { TextArea } = Input;

export default function CreateRegularAddon({ open, onClose, addonToEdit, onSuccess }) {
    const { t } = useTranslation(["addons"]);
    const toast = useToast();
    const [form] = Form.useForm();
    const [carList, setCarList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [isRequired, setIsRequired] = useState(false);
    const [images, setImages] = useState([]);
    const [selectedCars, setSelectedCars] = useState([]);
    const [availableForAllCars, setAvailableForAllCars] = useState(false);
    const [addonsExistingImg, setAddonsExistingImg] = useState("");

    useEffect(() => {
        const fetchCars = async () => {
            try {
                const res = await apiClient.get("/api/car/list");
                const data = res?.data?.data?.result || [];

                setCarList(
                    data.map((car) => ({
                        label: `${car.carName} | ${car?.carNumber}`,
                        value: car.id,
                    })),
                );
            } catch (_err) {
                toast.error({ message: t("createAddons.failedToFetchCars") });
            }
        };
        fetchCars();
    }, [t, toast]);

    useEffect(() => {
        if (addonToEdit) {
            form.setFieldsValue({
                name: addonToEdit.name,
                desc: addonToEdit.desc || "",
                rate: addonToEdit.rate,
                quantity: addonToEdit.quantity,
                featureList: addonToEdit.featureList || [{}],
            });

            if (addonToEdit.cars && Array.isArray(addonToEdit.cars)) {
                if (addonToEdit.cars.length === 0) {
                    setAvailableForAllCars(true);
                    setSelectedCars([]);
                } else if (carList.length > 0) {
                    setAvailableForAllCars(false);
                    form.setFieldsValue({ cars: addonToEdit.cars });
                    setSelectedCars(addonToEdit.cars);
                }
            }

            setIsActive(addonToEdit.status === "active");
            setIsRequired(addonToEdit.isRequired ?? false);

            if (addonToEdit.image) {
                setImages([
                    {
                        uid: "-1",
                        name: "addon-image.png",
                        status: "done",
                        url: addonToEdit.image,
                    },
                ]);
                setAddonsExistingImg(addonToEdit.image);
            }
        } else {
            form.resetFields();
            form.setFieldsValue({ quantity: 1000 });
            setAvailableForAllCars(false);
            setAddonsExistingImg("");
            setImages([]);
            setSelectedCars([]);
            setIsActive(true);
            setIsRequired(false);
        }
    }, [addonToEdit, form, carList]);

    useEffect(() => {
        if (availableForAllCars || selectedCars.length > 0) {
            form.setFields([{ name: "cars", errors: [] }]);
        }
    }, [availableForAllCars, selectedCars, form]);

    const handleCancel = () => {
        onClose();
        form.resetFields();
        form.setFieldsValue({ quantity: 1000 });
        setImages([]);
        setSelectedCars([]);
        setAvailableForAllCars(false);
        setIsActive(true);
        setIsRequired(false);
        setLoading(false);
        setAddonsExistingImg("");
    };

    // eslint-disable-next-line consistent-return
    const onFinish = async (values) => {
        if (images.length < 1) {
            return toast.error({ message: t("createAddons.imageRequired") });
        }

        if (values.rate < 0 || values.quantity < 0) {
            return toast.error({ message: t("createAddons.negativeValuesError") });
        }

        if (!availableForAllCars && (!values.cars || values.cars.length === 0)) {
            return toast.error({ message: t("createAddons.selectCarsError") });
        }

        const submitValues = { ...values, quantity: values.quantity || 1000 };
        if (availableForAllCars) {
            submitValues.cars = [];
        }

        setLoading(true);
        try {
            let imageUrl;
            if (addonToEdit && images[0]?.url && !images[0]?.originFileObj) {
                imageUrl = images[0].url;
            } else {
                imageUrl = await uploadFile(images[0]?.originFileObj);
                if (imageUrl && addonsExistingImg) {
                    await deleteFile(addonsExistingImg);
                }
            }

            if (addonToEdit) {
                const resp = await apiClient.patch(regularAddonApi.update(addonToEdit.id), {
                    ...submitValues,
                    status: isActive ? "active" : "inactive",
                    isRequired,
                    image: imageUrl,
                });

                if (resp?.data?.success) {
                    toast.success({ message: t("addonUpdatedSuccess") });
                    onSuccess();
                    handleCancel();
                }
            } else {
                const resp = await apiClient.post(regularAddonApi.create(), {
                    ...submitValues,
                    type: "regular",
                    status: isActive ? "active" : "inactive",
                    isRequired,
                    image: imageUrl,
                });

                if (resp?.data?.success) {
                    toast.success({ message: t("addonCreatedSuccess") });
                    onSuccess();
                    handleCancel();
                }
            }
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error({ message: t("createAddons.notAuthorized") });
            } else {
                toast.error({
                    message: addonToEdit ? t("createAddons.errorUpdating") : t("createAddons.savingError"),
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const title = addonToEdit ? t("editAddon") : t("createNewAddon");

    return (
        <Modal
            open={open}
            title={title}
            centered
            onOk={() => form.submit()}
            okButtonProps={{ loading, className: "!h-11 lg:!h-auto" }}
            cancelButtonProps={{ disabled: loading, className: "!h-11 lg:!h-auto" }}
            okText={addonToEdit ? t("createAddons.updateAddon") : t("createAddons.saveAddon")}
            onCancel={handleCancel}
            closeIcon
            maskClosable={false}
            width={640}>
            <div className="mb-4 flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">{t("createAddons.requiredLabel")}</span>
                    <ConfigProvider theme={{ token: { colorPrimary: "#f59e0b" } }}>
                        <Switch
                            checkedChildren={t("createAddons.switchOn")}
                            unCheckedChildren={t("createAddons.switchOff")}
                            onChange={setIsRequired}
                            checked={isRequired}
                        />
                    </ConfigProvider>
                </div>
                <ConfigProvider theme={{ token: { colorPrimary: "#36d649" } }}>
                    <Switch
                        checkedChildren={t("createAddons.switchOn")}
                        unCheckedChildren={t("createAddons.switchOff")}
                        onChange={setIsActive}
                        checked={isActive}
                    />
                </ConfigProvider>
            </div>

            <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                initialValues={{ quantity: 1000 }}
                requiredMark={customizeRequiredMark}>
                <Form.Item label={t("createAddons.image")} tooltip={t("createAddons.imageTooltip")}>
                    <ImageUpload maxCount={1} handleFileChange={setImages} fileList={images} />
                </Form.Item>

                <Form.Item
                    label={t("name")}
                    name="name"
                    rules={[{ required: true, message: t("createAddons.nameRequired") }]}>
                    <Input placeholder={t("createAddons.enterName")} />
                </Form.Item>

                <Form.Item
                    label={t("description")}
                    name="desc"
                    rules={[{ required: true, message: t("createAddons.descriptionRequired") }]}>
                    <TextArea rows={4} placeholder={t("createAddons.enterDescription")} />
                </Form.Item>

                <Form.Item
                    label={t("rate")}
                    name="rate"
                    tooltip={t("createAddons.rateTooltip")}
                    rules={[{ required: true, message: t("createAddons.rateRequired") }]}>
                    <InputNumber style={{ width: "100%" }} min={1} placeholder={t("createAddons.enterRate")} />
                </Form.Item>

                <Form.Item
                    label={t("quantity")}
                    name="quantity"
                    rules={[{ required: true, message: t("createAddons.quantityRequired") }]}>
                    <InputNumber style={{ width: "100%" }} min={1} placeholder={t("createAddons.enterQuantity")} />
                </Form.Item>

                <div>
                    <div className="mb-2 flex justify-between items-center">
                        <p>
                            {t("createAddons.selectCars")}
                            {!availableForAllCars && <span style={{ color: "red" }}>*</span>}
                        </p>
                        <div className="flex items-center gap-2">
                            <p style={{ fontSize: "0.9em" }}>{t("createAddons.availableForAllCars")}</p>
                            <Switch
                                size="small"
                                checked={availableForAllCars}
                                onChange={(checked) => {
                                    setAvailableForAllCars(checked);
                                    if (checked) {
                                        setSelectedCars([]);
                                        form.setFieldsValue({ cars: [] });
                                    }
                                }}
                            />
                        </div>
                    </div>
                    <Form.Item
                        name="cars"
                        rules={[
                            {
                                required: !availableForAllCars,
                                message: t("createAddons.selectCarsError"),
                            },
                        ]}>
                        <Select
                            mode="multiple"
                            placeholder={
                                availableForAllCars
                                    ? t("createAddons.availableForAllCarsPlaceholder")
                                    : t("createAddons.selectCarsPlaceholder")
                            }
                            options={carList}
                            allowClear
                            showSearch
                            disabled={availableForAllCars}
                            optionLabelProp="label"
                            optionFilterProp="label"
                            value={selectedCars}
                            onChange={(value) => {
                                setSelectedCars(value);
                                form.setFieldsValue({ cars: value });
                            }}
                            filterOption={(input, option) =>
                                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent={t("createAddons.noMatchingCars")}
                            style={{ width: "100%" }}
                            listHeight={250}
                        />
                    </Form.Item>
                </div>
            </Form>
        </Modal>
    );
}

CreateRegularAddon.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    addonToEdit: PropTypes.object,
    onSuccess: PropTypes.func.isRequired,
};
